import { memo, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  BRIEF_TYPE_ICON,
  BRIEF_TYPE_LABEL,
  useUpdateBriefCustomColumnValueMutation,
  type BriefType,
} from '@/entities/brief';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import {
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  TableCell,
  TableRow,
} from '@monksflow/monks-ui';
import { EllipsisIcon, FileText, PencilIcon, Trash2, type LucideIcon } from 'lucide-react';
import { DragHandle } from '@/shared/ui';

import {
  COMMENTS_COLUMN_WIDTH,
  type CustomColumn,
  type CustomColumnType,
  type StatusOption,
} from '../../lib/useTrackersBoard';
import { CellContextMenu } from '../CellContextMenu';
import { cellHasValue, isCopyableType } from '../../lib/useCellClipboard';
import { CommentsBadge } from '../CommentsBadge';
import {
  ApprovalCustomCell,
  type ApprovalValue,
  AssetsCustomCell,
  ButtonCustomCell,
  CheckboxCustomCell,
  DateCustomCell,
  DateRangeCustomCell,
  type DateRangeValue,
  FilesCustomCell,
  type FileLink,
  FormulaCustomCell,
  LinkCustomCell,
  NumberCustomCell,
  PeopleCustomCell,
  ProgressCustomCell,
  ProjectCustomCell,
  ReadOnlyCell,
  StatusCustomCell,
  TagsCustomCell,
  TeamCustomCell,
  TextCustomCell,
  TimelineCustomCell,
  type TimelinePhase,
  AiCellMarker,
} from '../CustomCells';
import type { TrackerCellMeta } from '@/entities/tracker-automation';
import { EM_DASH } from '../CustomCells/constants';
import { useCanEditTracker } from '../../lib/tracker-access';
import { STICKY_EDGE_SHADOW } from '../../lib/sticky-edge';
import { track } from '@/shared/analytics';
import { useTranslation } from 'react-i18next';

/** Column types whose cell has its own rendering even when nothing in it can be written. */
const DISPLAY_ONLY_TYPES: readonly CustomColumnType[] = [
  'brief_type',
  'assets',
  'project',
  'formula',
  'button',
];

/**
 * A board row, source-agnostic. Briefs pass their intrinsic fields
 * (`projectId`, `platformProjectId`, `briefType`) which unlock comments, the
 * read-only brief-type and project-assets columns, and per-brief cell
 * mutation; free-form tracker items omit them and instead supply
 * `onSetCellValue` + an editable title. Both render through this single
 * component so the two boards look identical.
 */
export interface BoardRowItem {
  id: string;
  title: string;
  customColumnValues?: Record<string, unknown> | null;
  /** Cells an AI action wrote, which carry a mark saying so. */
  customColumnMeta?: Record<string, TrackerCellMeta> | null;
  /**
   * Briefs only — workspace-api INTERNAL project id. Enables the brief_type
   * column and identifies the project for title lookup. NOT valid for
   * project-scoped API calls (use {@link platformProjectId}).
   */
  projectId?: string;
  /**
   * Briefs only — platform project id, the id `projects/:platformProjectId/*`
   * routes are scoped to. Required for the inline brief mutation and the assets
   * cell; `brief.projectId` (internal) 403s against those routes.
   */
  platformProjectId?: string;
  projectTitle?: string | null;
  /** Briefs only — drives the read-only `brief_type` column badge. */
  briefType?: BriefType | null;
  /**
   * The record is past its deadline and still running. Half of what a projects
   * board exists to answer, so it is marked on the row itself rather than left
   * to be read off a date column the user may have moved out of view.
   */
  overdue?: boolean;
  /**
   * This row has somewhere to go. Absent means it has, which is every board
   * whose rows share one destination. A record board answers per row, because
   * a record can be missing an id its own route needs while its neighbours are
   * not, and a row that only looks clickable is worse than one that does not.
   */
  routable?: boolean;
  /** The row offers its comment thread. Absent means it does. */
  commentable?: boolean;
}

interface TrackersBriefRowProps {
  item: BoardRowItem;
  columnOrder: string[];
  columnWidths: Record<string, number>;
  itemColumnWidth: number;
  columnDefs: Record<string, { label: string; minWidth: number; type?: CustomColumnType }>;
  customColumns: CustomColumn[];
  /** Navigate/open the row (briefs → brief detail). */
  onClick?: (id: string) => void;
  /**
   * Show the row's file instead of going to it, wired to the read-only `files`
   * cell - the thumbnail a board draws for a record that IS a file. That cell
   * is the one place on the row where a person is already looking at the
   * content, so asking for a closer look should not cost them the board.
   *
   * Absent on a board with no preview to show, where the cell keeps the row's
   * own destination like every other read-only cell.
   */
  onOpenPreview?: (id: string) => void;
  /** Editable title (free-form items). */
  editableTitle?: boolean;
  onRename?: (id: string, next: string) => void;
  /**
   * Cell-value writer override; when absent the brief mutation is used. Takes
   * `itemId` (not bound per-row) so the parent can pass one stable function to
   * every row — a per-row closure would defeat `memo` and re-render the whole
   * window on each scroll frame.
   */
  onSetCellValue?: (itemId: string, columnId: string, value: unknown) => void;
  /**
   * Which columns accept an edit. Absent means all of them, which is every
   * board whose cells are its own. A record board owns only some of its
   * columns, and the rest render as values instead of as controls, as does a
   * record field on a row whose record the caller may not change. Must be
   * stable for the same reason `onSetCellValue` is.
   */
  isColumnEditable?: (columnId: string, itemId: string) => boolean;
  /**
   * Rows can be dragged into a new order. False on a board the server orders,
   * where a drag would animate and then snap back.
   */
  reorderable?: boolean;
  /** The tracker this row belongs to — keys the row's comment thread. */
  trackerId?: string;
  /**
   * Whether the tracker has enabled automations (drives the brief mutation's
   * delayed re-fetch). Passed in — computed once at the group instead of a
   * `useQuery` per row, which was 1 query observer × every mounted row.
   */
  hasEnabledAutomations?: boolean;
  /** Comments column. Defaults to `true` when the item has a projectId. */
  showComments?: boolean;
  onOpenComments?: (id: string) => void;
  /**
   * Whether the open comments panel is showing THIS row, so the badge can hold
   * itself pressed. A boolean rather than the open row's key, so opening the
   * panel re-renders the two rows whose answer changed instead of every row on
   * a board that can hold 1500 of them.
   */
  isCommentsPanelOpen?: boolean;
  /** Optional per-row delete (free-form items). */
  onDelete?: (id: string) => void;
  /**
   * When the board drives item drags through a shared `DragOverlay` (custom
   * trackers, so items can cross groups), the dragged row must stay put and
   * only dim — the overlay is what follows the cursor. Without this the row
   * would move under its own transform AND the overlay would float: two
   * things moving at once.
   */
  dragWithOverlay?: boolean;
}

/**
 * Row controls stay hidden until the row is hovered or the control itself is
 * focused, so a keyboard user can still reach them.
 */
const ROW_ACTION_REVEAL =
  'shrink-0 opacity-0 transition-[opacity,background-color,color] group-hover/row:opacity-100 focus-visible:opacity-100';

/**
 * The reveal plus the button chrome `DragHandle` already brings on its own, so
 * the three controls in the cell match without restyling the shared handle.
 */
const ROW_ACTION_CLASS = `${ROW_ACTION_REVEAL} flex h-6 items-center justify-center rounded-md text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`;

interface RowAction {
  label: string;
  Icon: LucideIcon;
  destructive?: boolean;
  run: () => void;
}

function TrackersBriefRowImpl(props: TrackersBriefRowProps) {
  const { t } = useTranslation('trackers');

  const {
    item,
    columnOrder,
    columnWidths,
    itemColumnWidth,
    columnDefs,
    customColumns,
    onClick,
    onOpenPreview,
    editableTitle: editableTitleProp = false,
    onRename,
    onSetCellValue,
    isColumnEditable,
    reorderable: reorderableProp = true,
    trackerId,
    hasEnabledAutomations = false,
    onOpenComments,
    isCommentsPanelOpen = false,
    onDelete: onDeleteProp,
    dragWithOverlay = false,
  } = props;
  const canEdit = useCanEditTracker();
  const editableTitle = canEdit && editableTitleProp;
  const reorderable = canEdit && reorderableProp;
  const onDelete = canEdit ? onDeleteProp : undefined;
  const showComments = props.showComments ?? Boolean(item.projectId);

  const columnById = new Map<string, CustomColumn>(customColumns.map((c) => [c.id, c]));

  const workspaceId = useCurrentWorkspaceId() ?? '';
  // Always called (hooks rule); only used when no `onSetCellValue` override.
  const updateBriefValue = useUpdateBriefCustomColumnValueMutation({
    workspaceId,
    // Scoped route needs the platform id; item.projectId is the internal id.
    projectId: item.platformProjectId ?? '',
    briefId: item.id,
    refetchForAutomations: hasEnabledAutomations,
  });
  const setValue = (columnId: string, next: unknown) => {
    if (onSetCellValue) onSetCellValue(item.id, columnId, next);
    else updateBriefValue.mutate({ columnId, value: next });
  };

  const customValues = item.customColumnValues ?? {};

  // Right-click Copy/Paste/Clear on editable custom cells (monday.com-style).
  const boardType: 'briefs' | 'free_form' = onSetCellValue ? 'free_form' : 'briefs';

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });
  const sortableStyle = {
    // With a shared DragOverlay the overlay follows the cursor, so the source
    // row must not also translate — just dim it (opacity below).
    transform: dragWithOverlay && isDragging ? undefined : CSS.Translate.toString(transform),
    transition,
  };

  // Rename lives on the title itself; the overflow menu drives the same state
  // so both entry points end in one inline editor.
  const [renaming, setRenaming] = useState(false);
  // A closing menu reclaims focus from whatever the selected action opened, so
  // an editor opened during `onSelect` would blur, commit and close in the same
  // breath. Rename therefore only records intent here and opens once the menu
  // has finished closing.
  const renameRequested = useRef(false);

  const moreActions = (
    [
      editableTitle && onRename
        ? {
            label: t('trackersBoard.trackersBriefRow.rename'),
            Icon: PencilIcon,
            run: () => {
              renameRequested.current = true;
            },
          }
        : null,
      onDelete
        ? {
            label: t('trackersBoard.trackersBriefRow.delete'),
            Icon: Trash2,
            destructive: true,
            run: () => onDelete(item.id),
          }
        : null,
    ] as Array<RowAction | null>
  ).filter((action): action is RowAction => action !== null);

  const TypeIcon = item.briefType ? BRIEF_TYPE_ICON[item.briefType] : null;
  const briefTypeBadge: ReactNode = item.briefType ? (
    <Badge variant="outline" roundness="full" className="gap-1.5 bg-transparent">
      {TypeIcon ? <TypeIcon className="h-3 w-3" aria-hidden="true" /> : null}
      <span>{BRIEF_TYPE_LABEL[item.briefType]}</span>
    </Badge>
  ) : (
    <span className="text-muted-foreground">{EM_DASH}</span>
  );

  // A row the board has no route to is left as plain values: no pointer, no
  // cell buttons, and nothing announcing a destination that does not exist.
  const openRow = onClick && item.routable !== false ? onClick : undefined;

  /**
   * The cell that opens the preview: the row's read-only `files` column, which
   * is the thumbnail. Resolved from the column's own type rather than passed
   * down, so a board turns the behaviour on by handing over a handler and
   * nothing has to agree on a column id.
   */
  const previewColumnId = onOpenPreview
    ? columnOrder.find(
        (colId) =>
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          columnDefs[colId]?.type === 'files' && !(isColumnEditable?.(colId, item.id) ?? true),
      )
    : undefined;

  // Every read-only cell in a row follows the same route, so the first one is
  // the row's tab stop and the rest stay mouse targets. The preview cell is not
  // among them - it goes somewhere else, and keeps a stop of its own below.
  const keyboardOpenerColumnId = openRow
    ? columnOrder.find(
        (colId) =>
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          columnDefs[colId] !== undefined &&
          colId !== previewColumnId &&
          !(isColumnEditable?.(colId, item.id) ?? true),
      )
    : undefined;

  return (
    <TableRow
      ref={setNodeRef}
      style={sortableStyle}
      data-testid="trackers-brief-row"
      className={`group/row text-sm text-foreground ${isDragging ? 'opacity-50' : ''}`}
    >
      <TableCell
        className={`sticky left-0 z-10 h-12 border-t border-border-light bg-background group-hover/row:bg-accent ${STICKY_EDGE_SHADOW} ${
          openRow ? 'cursor-pointer' : ''
        }`}
        style={{ width: `${itemColumnWidth}px` }}
        onClick={openRow ? () => openRow(item.id) : undefined}
      >
        <div className="flex items-center gap-2 pl-1">
          {reorderable ? (
            <DragHandle
              ariaLabel={t('trackersBoard.trackersBriefRow.dragItem', { title: item.title })}
              title={t('trackersBoard.trackersBriefRow.dragToReorder')}
              attributes={attributes}
              listeners={listeners}
              onClick={(e) => e.stopPropagation()}
              className={`${ROW_ACTION_REVEAL} w-4.5`}
            />
          ) : null}
          {/* The document icon denotes a brief; free-form text items don't get it. */}
          {item.projectId ? (
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted">
              <FileText className="h-4 w-4 text-foreground" aria-hidden="true" />
            </div>
          ) : null}
          <ItemTitle
            title={item.title}
            editable={editableTitle}
            editing={renaming}
            onEditingChange={setRenaming}
            onRename={onRename ? (next) => onRename(item.id, next) : undefined}
          />
          {item.overdue ? (
            <Badge variant="destructive" roundness="full" className="shrink-0 font-normal">
              {t('trackersBoard.trackersBriefRow.late')}
            </Badge>
          ) : null}
          <span className="ml-auto flex shrink-0 items-center gap-0.5">
            {onDelete ? (
              <button
                type="button"
                aria-label={t('trackersBoard.trackersBriefRow.deleteItem', { title: item.title })}
                className={`${ROW_ACTION_CLASS} size-6 hover:bg-destructive/10 hover:text-destructive`}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(item.id);
                }}
              >
                <Trash2 className="size-3.5" aria-hidden />
              </button>
            ) : null}
            {moreActions.length > 0 ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label={t('trackersBoard.trackersBriefRow.moreActionsFor', {
                      title: item.title,
                    })}
                    title={t('trackersBoard.trackersBriefRow.moreActions')}
                    // The menu outlives the hover that opened it, so the
                    // trigger has to stay visible while it is open.
                    className={`${ROW_ACTION_CLASS} size-6 hover:bg-accent data-[state=open]:opacity-100`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <EllipsisIcon className="size-3.5" aria-hidden />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-44"
                  // Radix portals this to the body, but React still routes the
                  // event through the tree it was written in - and the cell
                  // that wraps it carries the row's navigate handler. Without
                  // this, choosing any action also opens the row.
                  onClick={(e) => e.stopPropagation()}
                  onCloseAutoFocus={(e) => {
                    if (!renameRequested.current) return;
                    renameRequested.current = false;
                    e.preventDefault();
                    setRenaming(true);
                  }}
                >
                  {moreActions.map(({ label, Icon, destructive, run }) => (
                    <DropdownMenuItem
                      key={label}
                      variant={destructive ? 'destructive' : 'default'}
                      onSelect={run}
                    >
                      <Icon className="size-4" aria-hidden />
                      {label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </span>
        </div>
      </TableCell>

      {showComments ? (
        <TableCell
          className={`sticky z-10 border-t border-border-light bg-background p-0 group-hover/row:bg-accent ${STICKY_EDGE_SHADOW}`}
          style={{
            left: `${itemColumnWidth}px`,
            width: COMMENTS_COLUMN_WIDTH,
            minWidth: COMMENTS_COLUMN_WIDTH,
          }}
        >
          <div className="flex items-center justify-center px-1 py-1.5">
            {trackerId && item.commentable !== false ? (
              <CommentsBadge
                trackerId={trackerId}
                rowKey={item.id}
                isPanelOpen={isCommentsPanelOpen}
                onClick={() => onOpenComments?.(item.id)}
              />
            ) : null}
          </div>
        </TableCell>
      ) : null}

      {columnOrder.map((colId) => {
        const def = columnDefs[colId];
        // `columnOrder` can briefly reference an id absent from `columnDefs`
        // (a just-added column before `customColumns` settles); skip rather
        // than crash. The index type hides that this can be undefined at runtime.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!def) return null;
        const isBriefType = def.type === 'brief_type';
        const isAssets = def.type === 'assets';
        const isProject = def.type === 'project';
        const isButton = def.type === 'button';
        const isFormula = def.type === 'formula';
        const column = columnById.get(colId);
        const statusOptions = column?.statusOptions ?? [];
        // A column the board itself would let you write, held read-only for a
        // view-only member. The display-only types keep their own rendering,
        // since there is nothing in them to write.
        const locked =
          !canEdit && !(def.type !== undefined && DISPLAY_ONLY_TYPES.includes(def.type));
        const editable = (isColumnEditable?.(colId, item.id) ?? true) && !locked;
        const aiMeta = item.customColumnMeta?.[colId];
        const cellBody =
          locked && drawsOwnReadOnlyCell(def.type, customValues[colId]) ? (
            renderCustomCell(
              def.type,
              customValues[colId],
              () => undefined,
              statusOptions,
              column?.clearable,
              true,
            )
          ) : !editable ? (
            <ReadOnlyCell
              type={def.type}
              value={customValues[colId]}
              statusOptions={statusOptions}
              fileType={customValues.fileType}
            />
          ) : isBriefType && item.projectId ? (
            briefTypeBadge
          ) : isAssets && item.platformProjectId ? (
            // Assets cell hits project-scoped routes → platform id.
            <AssetsCustomCell projectId={item.platformProjectId} />
          ) : isProject ? (
            <ProjectCustomCell projectId={item.projectId} title={item.projectTitle} />
          ) : isButton ? (
            <ButtonCustomCell
              caption={column?.buttonCaption ?? def.label}
              disabled={!canEdit}
              onPress={() => {
                // Writing a fresh nonce is what the engine reads as a click.
                setValue(colId, new Date().toISOString());
                track('tracker_button_clicked', {
                  tracker_id: trackerId ?? '',
                  board_type: onSetCellValue ? 'free_form' : 'briefs',
                  column_id: colId,
                });
              }}
            />
          ) : isFormula ? (
            // Reads the whole row: the expression names other columns, so this
            // cell cannot be driven by its own value alone.
            <FormulaCustomCell
              expression={column?.formulaExpression}
              columns={customColumns}
              values={customValues}
            />
          ) : (
            renderCustomCell(
              def.type,
              customValues[colId],
              (next) => setValue(colId, next),
              statusOptions,
              column?.clearable,
            )
          );
        const content = aiMeta ? <AiCellMarker meta={aiMeta}>{cellBody}</AiCellMarker> : cellBody;
        return (
          <TableCell
            key={colId}
            className="border-t border-border-light p-0 align-middle"
            style={{
              width: columnWidths[colId] ? `${columnWidths[colId]}px` : undefined,
              minWidth: def.minWidth,
            }}
          >
            {/* No context menu on a read-only cell: its Paste and Clear would
                call a writer the board discards, i.e. menu items that look
                available and silently do nothing. */}
            {editable && canEdit && isCopyableType(def.type) ? (
              <CellContextMenu
                columnType={def.type}
                value={customValues[colId]}
                hasValue={cellHasValue(def.type, customValues[colId])}
                clearable={column?.clearable}
                boardType={boardType}
                onChange={(next) => setValue(colId, next)}
              >
                {content}
              </CellContextMenu>
            ) : colId === previewColumnId && onOpenPreview ? (
              // The one read-only cell that does not follow the row's route.
              // It keeps a tab stop of its own rather than borrowing the row's:
              // the destination differs, so collapsing the two would hide it
              // from a keyboard entirely.
              <button
                type="button"
                className="flex w-full cursor-zoom-in items-center justify-center overflow-hidden px-2 py-1.5"
                onClick={() => onOpenPreview(item.id)}
              >
                {/*
                  The thumbnail is an `img` whose alt is the column's own label,
                  which would otherwise join the name below and announce the
                  button as "Preview Preview hero.png". Hidden rather than
                  relabelled: the picture is what the sr-only name describes.
                */}
                <span aria-hidden="true" className="flex min-w-0 items-center">
                  {content}
                </span>
                <span className="sr-only">
                  {t('trackersBoard.trackersBriefRow.previewItem', { title: item.title })}
                </span>
              </button>
            ) : !editable && openRow ? (
              // A cell the board cannot write is otherwise a dead click. On a
              // row that has somewhere to go it goes there instead, which is
              // what turns an asset's approval state from a chip that ignores
              // you into the route to the place you can act on it. A button
              // rather than a clickable box, so the same route exists for a
              // keyboard. The destination is appended to the cell's own text
              // rather than replacing it with an `aria-label`: the value is the
              // thing being read out, and a row of buttons all named "Open
              // <row>" tells a screen reader nothing about which is which.
              <button
                type="button"
                // Every read-only cell in the row opens the same thing, so one
                // of them is the row's tab stop and the others are left as
                // mouse targets rather than a dozen identical stops per row.
                tabIndex={colId === keyboardOpenerColumnId ? undefined : -1}
                className="flex w-full cursor-pointer items-center justify-center overflow-hidden px-2 py-1.5"
                onClick={() => openRow(item.id)}
              >
                {content}
                <span className="sr-only">
                  {t('trackersBoard.trackersBriefRow.openItem', { title: item.title })}
                </span>
              </button>
            ) : (
              <div className="flex items-center justify-center overflow-hidden px-2 py-1.5">
                {content}
              </div>
            )}
          </TableCell>
        );
      })}
      {/* Empty cell beneath the header's `+` button — preserves grid alignment. */}
      <TableCell
        className="border-t border-border-light bg-background group-hover/row:bg-accent"
        style={{ width: 40, minWidth: 40 }}
      />
      {/* Spacer — keeps the row's rule running to the right edge. */}
      <TableCell className="border-t border-border-light bg-background group-hover/row:bg-accent" />
    </TableRow>
  );
}

/**
 * Memoized so scrolling the virtualized board re-renders only the rows entering
 * the window, not every mounted row. Relies on the parent passing stable props
 * (notably a single `onSetCellValue` that takes `itemId`, not a per-row closure)
 * — otherwise the shallow prop compare never bails and the win is lost.
 */
export const TrackersBriefRow = memo(TrackersBriefRowImpl);

/** Item title: read-only (briefs) or click-to-edit inline (free-form items). */
function ItemTitle({
  title,
  editable,
  editing,
  onEditingChange,
  onRename,
}: {
  title: string;
  editable: boolean;
  /** Owned by the row so the overflow menu's Rename opens this same editor. */
  editing: boolean;
  onEditingChange: (next: boolean) => void;
  onRename?: (next: string) => void;
}) {
  const { t } = useTranslation('trackers');

  const [draft, setDraft] = useState(title);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!editing) setDraft(title);
  }, [title, editing]);
  // Layout, not passive: the editor opens from a menu closing on a timer, so a
  // passive focus would land a tick after the input is already in the DOM and
  // anything reading focus at insertion sees the wrong element.
  useLayoutEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  if (!editable || !onRename) {
    return (
      <span className="truncate" title={title}>
        {title}
      </span>
    );
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          onEditingChange(false);
          const next = draft.trim();
          if (next && next !== title) onRename(next);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') {
            setDraft(title);
            onEditingChange(false);
          }
        }}
        className="min-w-0 flex-1 rounded border border-ring bg-background px-1.5 py-0.5 text-sm text-foreground focus:outline-none"
      />
    );
  }

  return (
    <button
      type="button"
      title={t('trackersBoard.trackersBriefRow.clickToRename')}
      onClick={(e) => {
        e.stopPropagation();
        onEditingChange(true);
      }}
      className="truncate text-left hover:underline"
    >
      {title}
    </button>
  );
}

/**
 * Values `ReadOnlyCell` cannot draw, which the column's own cell shows in its
 * read-only mode instead. A record board's `files` value is a preview URL,
 * which `ReadOnlyCell` does draw; a free-form board's is a list of links.
 */
const READ_ONLY_TYPED_CELLS: readonly CustomColumnType[] = [
  'date_range',
  'timeline',
  'progress',
  'approval',
];

function drawsOwnReadOnlyCell(type: CustomColumnType | undefined, value: unknown): boolean {
  if (type === 'files') return Array.isArray(value);
  return type !== undefined && READ_ONLY_TYPED_CELLS.includes(type);
}

function renderCustomCell(
  type: CustomColumnType | undefined,
  rawValue: unknown,
  onChange: (next: unknown) => void,
  statusOptions: StatusOption[],
  statusClearable: boolean | undefined,
  readOnly = false,
): ReactNode {
  switch (type) {
    case 'text':
      return (
        <TextCustomCell
          value={typeof rawValue === 'string' ? rawValue : undefined}
          onChange={onChange}
        />
      );
    case 'number':
      return (
        <NumberCustomCell
          value={typeof rawValue === 'number' ? rawValue : undefined}
          onChange={onChange}
        />
      );
    case 'date':
      return (
        <DateCustomCell
          value={typeof rawValue === 'string' ? rawValue : undefined}
          onChange={onChange}
        />
      );
    case 'status':
      return (
        <StatusCustomCell
          value={typeof rawValue === 'string' ? rawValue : undefined}
          onChange={onChange}
          options={statusOptions}
          clearable={statusClearable}
        />
      );
    case 'tags': {
      const tags = Array.isArray(rawValue)
        ? rawValue.filter((v): v is string => typeof v === 'string')
        : undefined;
      return <TagsCustomCell value={tags} onChange={onChange} />;
    }
    case 'people': {
      const ids = Array.isArray(rawValue)
        ? rawValue.filter((v): v is string => typeof v === 'string')
        : undefined;
      return <PeopleCustomCell value={ids} onChange={onChange} />;
    }
    case 'checkbox':
      return <CheckboxCustomCell value={rawValue === true} onChange={onChange} />;
    case 'link':
      return (
        <LinkCustomCell
          value={typeof rawValue === 'string' ? rawValue : undefined}
          onChange={onChange}
        />
      );
    case 'date_range': {
      const range =
        rawValue && typeof rawValue === 'object' ? (rawValue as DateRangeValue) : undefined;
      return <DateRangeCustomCell value={range} onChange={onChange} readOnly={readOnly} />;
    }
    case 'timeline': {
      const phases = Array.isArray(rawValue) ? (rawValue as TimelinePhase[]) : undefined;
      return <TimelineCustomCell value={phases} onChange={onChange} readOnly={readOnly} />;
    }
    case 'team':
      return (
        <TeamCustomCell
          value={typeof rawValue === 'string' ? rawValue : undefined}
          onChange={onChange}
        />
      );
    case 'progress':
      return (
        <ProgressCustomCell
          value={typeof rawValue === 'number' ? rawValue : undefined}
          onChange={onChange}
          readOnly={readOnly}
        />
      );
    case 'files': {
      const files = Array.isArray(rawValue) ? (rawValue as FileLink[]) : undefined;
      return <FilesCustomCell value={files} onChange={onChange} readOnly={readOnly} />;
    }
    case 'approval': {
      const approval =
        rawValue && typeof rawValue === 'object' ? (rawValue as ApprovalValue) : undefined;
      return <ApprovalCustomCell value={approval} onChange={onChange} readOnly={readOnly} />;
    }
    default:
      return <span className="text-muted-foreground">{EM_DASH}</span>;
  }
}
